import { useEffect, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { LogOut, Trash2, RefreshCw, Mail, Radio, BookOpen, User, ExternalLink, CheckCircle2, XCircle, PlusCircle, Clock } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import GestionLink from "@/components/GestionLink";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Logo = () => (
  <img src="/logo-proteccion-civil-vigo.png" alt="Protección Civil Concello de Vigo" className="w-12 h-12 object-contain shrink-0" />
);

const STATUS = {
  pending: { label: "Pendiente", cls: "bg-amber-100 text-amber-800" },
  accepted: { label: "Aceptado", cls: "bg-green-100 text-green-800" },
  rejected: { label: "Rechazado", cls: "bg-red-100 text-red-700" },
};

export default function AdminPage() {
  const { user, logout } = useAuth();
  const [tab, setTab] = useState("solicitudes");
  const [requests, setRequests] = useState(null);
  const [enrollments, setEnrollments] = useState(null);
  const [courses, setCourses] = useState(null);
  const [newCourse, setNewCourse] = useState({ title: "", desc: "", lessons: "", instructors: "" });
  const [creating, setCreating] = useState(false);

  const loadRequests = async () => {
    try {
      const { data } = await axios.get(`${API}/contact?limit=200`, { withCredentials: true });
      setRequests(data);
    } catch {
      toast.error("No se pudieron cargar las solicitudes");
      setRequests([]);
    }
  };

  const loadEnrollments = async () => {
    try {
      const { data } = await axios.get(`${API}/enrollments`, { withCredentials: true });
      setEnrollments(data);
    } catch {
      toast.error("No se pudieron cargar las matrículas");
      setEnrollments([]);
    }
  };

  const loadCourses = async () => {
    try {
      const { data } = await axios.get(`${API}/courses`);
      setCourses(data);
    } catch {
      toast.error("No se pudieron cargar los cursos");
      setCourses([]);
    }
  };

  useEffect(() => {
    if (user && user.role === "admin") {
      loadRequests();
      loadEnrollments();
      loadCourses();
    }
  }, [user]);

  const removeRequest = async (id) => {
    try {
      await axios.delete(`${API}/contact/${id}`, { withCredentials: true });
      setRequests((prev) => prev.filter((r) => r.id !== id));
      toast.success("Solicitud eliminada");
    } catch {
      toast.error("No se pudo eliminar");
    }
  };

  const setEnrollmentStatus = async (id, status) => {
    try {
      const { data } = await axios.post(`${API}/enrollments/${id}/status`, { status }, { withCredentials: true });
      setEnrollments((prev) => prev.map((e) => (e.id === id ? data : e)));
      toast.success(status === "accepted" ? "Alumno aceptado en el curso" : "Matrícula actualizada");
    } catch {
      toast.error("No se pudo actualizar");
    }
  };

  const createCourse = async (e) => {
    e.preventDefault();
    setCreating(true);
    try {
      const { data } = await axios.post(`${API}/courses`, newCourse, { withCredentials: true });
      setCourses((prev) => [...(prev || []), data]);
      setNewCourse({ title: "", desc: "", lessons: "", instructors: "" });
      toast.success("Curso creado y publicado en el campus");
    } catch (err) {
      toast.error(err.response?.data?.detail || "No se pudo crear el curso");
    } finally {
      setCreating(false);
    }
  };

  const deleteCourse = async (id) => {
    try {
      await axios.delete(`${API}/courses/${id}`, { withCredentials: true });
      setCourses((prev) => prev.filter((c) => c.id !== id));
      toast.success("Curso eliminado");
    } catch {
      toast.error("No se pudo eliminar el curso");
    }
  };

  if (user === null) {
    return (
      <div className="min-h-screen bg-[#fffbf2] flex items-center justify-center">
        <div className="w-10 h-10 border-4 border-[#e65100] border-t-transparent rounded-full animate-spin" aria-label="Cargando" />
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  if (user.role !== "admin") return <Navigate to="/campus" replace />;

  const Spinner = () => (
    <div className="py-16 flex justify-center">
      <div className="w-10 h-10 border-4 border-[#e65100] border-t-transparent rounded-full animate-spin" aria-label="Cargando" />
    </div>
  );

  return (
    <div data-testid="admin-page" className="min-h-screen bg-[#fffbf2] text-[#0f2027]">
      <header className="bg-[#e65100] text-white px-6 py-4">
        <div className="max-w-6xl mx-auto flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="bg-white rounded-lg p-1"><Logo /></div>
            <div className="leading-none">
              <span className="block font-display text-sm tracking-wider uppercase">PROTECCIÓN CIVIL VIGO</span>
              <span className="block text-[10px] tracking-widest text-white/80 font-mono-tactical uppercase">
                Panel de Administración
              </span>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <GestionLink testId="admin-gestion-link" />
            <Link
              to="/campus"
              data-testid="admin-campus-link"
              className="hidden sm:block text-xs font-mono-tactical uppercase tracking-wider hover:underline"
            >
              Campus
            </Link>
            <button
              onClick={logout}
              data-testid="admin-logout-btn"
              className="flex items-center gap-2 bg-[#0f2027] px-4 py-2 text-xs font-mono-tactical uppercase tracking-wider font-semibold rounded hover:bg-[#162c35] transition-colors"
            >
              <LogOut className="w-3.5 h-3.5" aria-hidden="true" />
              Salir
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 py-12 space-y-8">
        <div className="flex flex-wrap gap-2 border-b border-[#0f2027]/10 pb-6" data-testid="admin-tabs">
          {[
            ["solicitudes", "Solicitudes"],
            ["matriculas", "Matrículas"],
            ["cursos", "Cursos"],
          ].map(([key, label]) => (
            <button
              key={key}
              onClick={() => setTab(key)}
              data-testid={`admin-tab-${key}`}
              className={`px-5 py-2.5 text-xs font-mono-tactical uppercase tracking-widest font-bold rounded transition-colors ${
                tab === key ? "bg-[#0f2027] text-white" : "bg-[#0f2027]/10 text-[#0f2027] hover:bg-[#0f2027]/20"
              }`}
            >
              {label}
            </button>
          ))}
        </div>

        {/* SOLICITUDES */}
        {tab === "solicitudes" && (
          <section className="space-y-6">
            <div className="flex items-end justify-between gap-6">
              <h1 className="font-display text-4xl uppercase tracking-tight">Solicitudes de contacto</h1>
              <button onClick={loadRequests} data-testid="admin-refresh-btn" className="flex items-center gap-2 border-2 border-[#0f2027] px-5 py-2.5 text-xs font-mono-tactical uppercase tracking-wider font-bold rounded hover:bg-[#0f2027] hover:text-white transition-colors">
                <RefreshCw className="w-3.5 h-3.5" aria-hidden="true" /> Actualizar
              </button>
            </div>
            {requests === null ? <Spinner /> : requests.length === 0 ? (
              <p className="text-sm text-[#0f2027]/60" data-testid="admin-empty-state">Sin solicitudes todavía.</p>
            ) : (
              <div className="space-y-4" data-testid="admin-requests-list">
                {requests.map((r) => (
                  <article key={r.id} data-testid={`admin-request-${r.id}`} className="p-6 bg-white border-2 border-[#0f2027] rounded-lg shadow-[6px_6px_0px_#0f2027] flex flex-col md:flex-row md:items-start justify-between gap-4">
                    <div className="space-y-2 min-w-0">
                      <div className="flex flex-wrap items-center gap-3">
                        <span className="font-display text-xl uppercase flex items-center gap-2">
                          <User className="w-4 h-4 text-[#e65100]" aria-hidden="true" />
                          {r.nombre}
                        </span>
                        <span className="text-xs font-mono-tactical bg-[#e65100]/10 text-[#e65100] px-2.5 py-1 rounded font-bold uppercase flex items-center gap-1.5">
                          <BookOpen className="w-3 h-3" aria-hidden="true" />
                          {r.curso}
                        </span>
                      </div>
                      <a href={`mailto:${r.email}`} className="text-sm text-[#e65100] hover:underline font-mono-tactical">{r.email}</a>
                      {r.mensaje && <p className="text-sm text-[#0f2027]/80 leading-relaxed">{r.mensaje}</p>}
                      <p className="text-[11px] font-mono-tactical text-[#0f2027]/50">{new Date(r.timestamp).toLocaleString("es-ES")}</p>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <a href={`mailto:${r.email}?subject=Re: ${encodeURIComponent(r.curso)}`} data-testid={`admin-reply-${r.id}`} className="px-4 py-2 bg-[#e65100] text-white text-xs font-mono-tactical uppercase tracking-wider font-bold rounded hover:bg-[#c2410c] transition-colors">
                        Responder
                      </a>
                      <button onClick={() => removeRequest(r.id)} data-testid={`admin-delete-${r.id}`} className="p-2 border-2 border-[#0f2027]/20 rounded hover:border-red-600 hover:text-red-600 transition-colors" aria-label="Eliminar solicitud">
                        <Trash2 className="w-4 h-4" aria-hidden="true" />
                      </button>
                    </div>
                  </article>
                ))}
              </div>
            )}
          </section>
        )}

        {/* MATRÍCULAS */}
        {tab === "matriculas" && (
          <section className="space-y-6">
            <div className="flex items-end justify-between gap-6">
              <h1 className="font-display text-4xl uppercase tracking-tight">Matrículas de cursos</h1>
              <button onClick={loadEnrollments} data-testid="admin-refresh-enrollments-btn" className="flex items-center gap-2 border-2 border-[#0f2027] px-5 py-2.5 text-xs font-mono-tactical uppercase tracking-wider font-bold rounded hover:bg-[#0f2027] hover:text-white transition-colors">
                <RefreshCw className="w-3.5 h-3.5" aria-hidden="true" /> Actualizar
              </button>
            </div>
            {enrollments === null ? <Spinner /> : enrollments.length === 0 ? (
              <p className="text-sm text-[#0f2027]/60" data-testid="admin-empty-enrollments">Todavía no hay solicitudes de matrícula.</p>
            ) : (
              <div className="space-y-4" data-testid="admin-enrollments-list">
                {enrollments.map((e) => (
                  <article key={e.id} data-testid={`admin-enrollment-${e.id}`} className="p-6 bg-white border-2 border-[#0f2027] rounded-lg shadow-[6px_6px_0px_#0f2027] flex flex-col md:flex-row md:items-center justify-between gap-4">
                    <div className="space-y-2 min-w-0">
                      <div className="flex flex-wrap items-center gap-3">
                        <span className="font-display text-xl uppercase flex items-center gap-2">
                          <User className="w-4 h-4 text-[#e65100]" aria-hidden="true" />
                          {e.user_name}
                        </span>
                        <span className={`text-xs font-mono-tactical px-2.5 py-1 rounded font-bold uppercase ${STATUS[e.status].cls}`} data-testid={`admin-enrollment-status-${e.id}`}>
                          {STATUS[e.status].label}
                        </span>
                      </div>
                      <p className="text-sm text-[#0f2027]/80 font-mono-tactical">{e.course_title}</p>
                      <p className="text-[11px] font-mono-tactical text-[#0f2027]/50">{e.user_email} · {new Date(e.created_at).toLocaleString("es-ES")}</p>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <button
                        onClick={() => setEnrollmentStatus(e.id, "accepted")}
                        data-testid={`admin-accept-${e.id}`}
                        className="flex items-center gap-2 px-4 py-2 bg-green-700 text-white text-xs font-mono-tactical uppercase tracking-wider font-bold rounded hover:bg-green-800 transition-colors"
                      >
                        <CheckCircle2 className="w-3.5 h-3.5" aria-hidden="true" />
                        Aceptar
                      </button>
                      <button
                        onClick={() => setEnrollmentStatus(e.id, "rejected")}
                        data-testid={`admin-reject-${e.id}`}
                        className="flex items-center gap-2 px-4 py-2 border-2 border-[#0f2027]/20 text-xs font-mono-tactical uppercase tracking-wider font-bold rounded hover:border-red-600 hover:text-red-600 transition-colors"
                      >
                        <XCircle className="w-3.5 h-3.5" aria-hidden="true" />
                        Rechazar
                      </button>
                    </div>
                  </article>
                ))}
              </div>
            )}
          </section>
        )}

        {/* CURSOS */}
        {tab === "cursos" && (
          <section className="space-y-6">
            <h1 className="font-display text-4xl uppercase tracking-tight">Gestión de cursos</h1>

            <form onSubmit={createCourse} data-testid="admin-create-course-form" className="p-8 bg-white border-2 border-[#0f2027] rounded-lg shadow-[6px_6px_0px_#0f2027] space-y-4">
              <h2 className="font-display text-xl uppercase flex items-center gap-2">
                <PlusCircle className="w-5 h-5 text-[#e65100]" aria-hidden="true" />
                Crear curso nuevo
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="sm:col-span-2">
                  <label className="block text-xs font-mono-tactical uppercase mb-1">Nombre del curso</label>
                  <input required minLength={3} value={newCourse.title} onChange={(e) => setNewCourse({ ...newCourse, title: e.target.value })} data-testid="admin-course-title-input" className="w-full px-4 py-3 text-sm bg-[#fffbf2] border border-[#0f2027]/30 rounded focus:border-[#e65100] outline-none" placeholder="Ej: Rescate en espacios confinados" />
                </div>
                <div>
                  <label className="block text-xs font-mono-tactical uppercase mb-1">Lecciones</label>
                  <input value={newCourse.lessons} onChange={(e) => setNewCourse({ ...newCourse, lessons: e.target.value })} data-testid="admin-course-lessons-input" className="w-full px-4 py-3 text-sm bg-[#fffbf2] border border-[#0f2027]/30 rounded focus:border-[#e65100] outline-none" placeholder="Ej: 12 Lecciones Online" />
                </div>
                <div>
                  <label className="block text-xs font-mono-tactical uppercase mb-1">Instructores (separados por comas)</label>
                  <input value={newCourse.instructors} onChange={(e) => setNewCourse({ ...newCourse, instructors: e.target.value })} data-testid="admin-course-instructors-input" className="w-full px-4 py-3 text-sm bg-[#fffbf2] border border-[#0f2027]/30 rounded focus:border-[#e65100] outline-none" placeholder="Ej: Javier Ferreira, Lucía Rivas" />
                </div>
                <div className="sm:col-span-2">
                  <label className="block text-xs font-mono-tactical uppercase mb-1">Descripción</label>
                  <textarea rows={2} value={newCourse.desc} onChange={(e) => setNewCourse({ ...newCourse, desc: e.target.value })} data-testid="admin-course-desc-input" className="w-full px-4 py-3 text-sm bg-[#fffbf2] border border-[#0f2027]/30 rounded focus:border-[#e65100] outline-none" placeholder="Qué aprenderán los alumnos..." />
                </div>
              </div>
              <button type="submit" disabled={creating} data-testid="admin-course-create-btn" className="px-6 py-3 bg-[#e65100] text-white text-xs font-mono-tactical uppercase tracking-widest font-bold rounded hover:bg-[#c2410c] transition-colors disabled:opacity-60">
                {creating ? "Creando..." : "Crear curso"}
              </button>
            </form>

            {courses === null ? <Spinner /> : courses.length === 0 ? (
              <p className="text-sm text-[#0f2027]/60">No hay cursos.</p>
            ) : (
              <div className="space-y-4" data-testid="admin-courses-list">
                {courses.map((c) => (
                  <article key={c.id} data-testid={`admin-course-${c.id}`} className="p-6 bg-white border-2 border-[#0f2027] rounded-lg shadow-[6px_6px_0px_#0f2027] flex flex-col md:flex-row md:items-center justify-between gap-4">
                    <div className="space-y-1 min-w-0">
                      <h3 className="font-display text-xl uppercase">{c.title}</h3>
                      <p className="text-xs font-mono-tactical text-[#0f2027]/60">
                        {[c.lessons, c.instructors].filter(Boolean).join(" · ") || "Sin detalles"}
                      </p>
                    </div>
                    <button onClick={() => deleteCourse(c.id)} data-testid={`admin-course-delete-${c.id}`} className="flex items-center gap-2 px-4 py-2 border-2 border-[#0f2027]/20 text-xs font-mono-tactical uppercase tracking-wider font-bold rounded hover:border-red-600 hover:text-red-600 transition-colors shrink-0">
                      <Trash2 className="w-3.5 h-3.5" aria-hidden="true" />
                      Eliminar
                    </button>
                  </article>
                ))}
              </div>
            )}
          </section>
        )}
      </main>

      <footer className="bg-[#0f2027] text-[#fffbf2]/60 py-8 px-6 text-xs font-mono-tactical border-t border-white/10">
        <div className="max-w-6xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4">
          <span className="uppercase tracking-wider">Protección Civil · Concello de Vigo</span>
          <span className="flex items-center gap-2">
            <Radio className="w-3.5 h-3.5 text-[#e65100]" aria-hidden="true" />
            info@busquedaysalvamento.es
          </span>
        </div>
      </footer>
    </div>
  );
}
